/**
 * The one place the SPA talks to Flask.
 *
 * SAME-ORIGIN BY DEFAULT. The API is a separate deployable, but the browser
 * reaches it through this origin — the frontend proxies the paths Flask still
 * owns (vite.config.ts in dev, nginx in the image). So API_BASE is empty and
 * calls are relative.
 *
 * `credentials: 'include'` is set anyway, on purpose: it is required the
 * moment anything talks to the API host directly, and forgetting it then
 * produces a 401 that reads like a login bug rather than a missing option.
 * Setting it always costs nothing and removes the trap.
 *
 * THREE RESPONSE SHAPES, tolerated deliberately. New endpoints answer the
 * {success, data} / {success, error} envelope from pass F1. Legacy endpoints
 * answer bare JSON, or {success, ...fields}. Retrofitting them mid-port turns a
 * port into a rewrite, so this unwraps what it recognizes and passes the rest
 * through. The tolerance is retired with the last legacy endpoint, not before.
 */

/** Where the API lives. Empty means "this origin", which is the norm; an
 *  absolute URL points the SPA straight at the API host instead. */
import { VIEW_AS_HEADER, setViewAsGrant, viewAsGrant } from './viewAs'

export const API_BASE: string = (import.meta.env.VITE_API_BASE ?? '')
  .replace(/\/$/, '')

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** The session is gone — the shell shows the signed-out state. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }
}

type Json = Record<string, unknown>

function unwrap(body: unknown): unknown {
  if (body && typeof body === 'object' && 'success' in body) {
    const b = body as Json
    if (b.success === true && 'data' in b) return b.data
    // {success: true, games: [...]} — legacy, already the payload.
    if (b.success === true) return b
  }
  return body
}

/**
 * Read a response body, tolerating one that is not JSON.
 *
 * A failed request must fail with its own status, so the parse never decides
 * the outcome. When a body is not JSON this throws an ApiError carrying the
 * real status, rather than letting `JSON.parse` raise a SyntaxError that
 * loses it: Cloud Run answers an out-of-memory kill with the plain text
 * "Service Unavailable", nginx answers a 502 with an HTML page, and either
 * one reaching `JSON.parse` first turns a diagnosable status into
 * `Unexpected token`.
 *
 * The body is never reused as the message. It is an untrusted upstream page,
 * and callers render `e.message` straight into toasts — echoing it there puts
 * raw markup and server banners ("nginx/1.25.3") in front of a customer. The
 * status and code carry the diagnosis instead.
 *
 * @throws {ApiError} when the body is non-empty and is not JSON.
 */
async function readBody(res: Response): Promise<unknown> {
  const text = await res.text()
  if (!text) return null // 204 and friends have no body to parse.
  try {
    return JSON.parse(text)
  } catch {
    if (res.ok) {
      throw new ApiError(res.status, 'malformed_response',
        'The server returned a response that was not JSON.')
    }
    // Same wording as errorFrom's fallback below, so a given status reads the
    // same way whether or not the error body happened to be JSON.
    throw new ApiError(res.status, 'upstream_error', `Request failed (${res.status})`)
  }
}

function errorFrom(status: number, body: unknown): ApiError {
  if (body && typeof body === 'object') {
    const b = body as Json
    const err = b.error
    if (err && typeof err === 'object') {
      const e = err as Json
      return new ApiError(status, String(e.code ?? 'error'), String(e.message ?? 'Request failed'))
    }
    if (typeof err === 'string') return new ApiError(status, 'error', err)
  }
  return new ApiError(status, 'error', `Request failed (${status})`)
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  return unwrap(await apiEnvelope(path, init)) as T
}

/** api(), but the body exactly as the server sent it — {success, data,
 *  metadata} intact. For endpoints whose metadata matters beside `data`:
 *  api() hands back `data` alone, and a caller reading `.data` off that
 *  gets undefined (the Workshop's match timeline read "No tactical score"
 *  on every stack game, 2026-10-15). */
/**
 * The club the shell is showing. The club itself lives in the auth cookie,
 * which every tab shares and which some staff pages move to load another
 * club's match; a page still showing the old club then asked for "our
 * matches" and got the other club's under its own name (Bradley's Matches
 * listing AFC Richmond's, prod 2026-10-23). So every call names the club it
 * is for (the server refuses a mismatch with 409 org_changed, which reloads
 * the page), and a switch made from any page moves this and tells the
 * shell (ORG_SWITCHED_EVENT), which reloads its session.
 */
let shownOrgId: number | null = null
export const ORG_HEADER = 'X-Priori-Org'
export const ORG_SWITCHED_EVENT = 'priori:org-switched'
const SWITCH_PATHS = ['/api/v1/auth/switch-organization', '/api/v1/auth/switch-to-game',
  '/api/v1/auth/switch-to-report']

/** Set by the shell from the session it renders. */
export function setShownOrg(id: number | null): void {
  shownOrgId = id
}

export async function apiEnvelope<T = unknown>(
  path: string,
  init: RequestInit = {},
): Promise<T> {
  const isWrite = !!init.method && init.method.toUpperCase() !== 'GET'
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    // Cross-origin cookie auth. Without this the API sees an anonymous caller.
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      // json_csrf_guard requires this content type on cookie-authenticated
      // writes: a cross-site HTML form cannot send it, and a cross-site fetch
      // that does is stopped by the browser's preflight.
      ...(isWrite ? { 'Content-Type': 'application/json' } : {}),
      ...(shownOrgId != null ? { [ORG_HEADER]: String(shownOrgId) } : {}),
      // this tab's "View as" (lib/viewAs): named here, not only by the boot
      // wrapper, so every api() caller carries it wherever it runs
      ...viewAsHeaders(),
      ...init.headers,
    },
  })

  const body = await readBody(res)

  if (!res.ok) {
    const err = errorFrom(res.status, body)
    if (res.status === 409 && err.code === 'org_changed') window.location.reload()
    throw err
  }
  noteOrgSwitch(path, body)
  return body as T
}

function viewAsHeaders(): Record<string, string> {
  const grant = viewAsGrant()
  return grant ? { [VIEW_AS_HEADER]: grant } : {}
}

/** A switch that landed moves the shown club and tells the shell. While
 *  this tab views as someone the server answers with the tab's new grant,
 *  in the new club: the tab keeps it (lib/viewAs). */
function noteOrgSwitch(path: string, body: unknown): void {
  if (!SWITCH_PATHS.some((p) => path.startsWith(p))) return
  const grant = (body as { view_as?: unknown } | null)?.view_as
  if (typeof grant === 'string' && grant) setViewAsGrant(grant)
  const id = (body as { organization_id?: unknown } | null)?.organization_id
  if (typeof id !== 'number' || id === shownOrgId) return
  shownOrgId = id
  window.dispatchEvent(new CustomEvent(ORG_SWITCHED_EVENT, { detail: id }))
}

/**
 * Multipart write. `api()` stamps Content-Type: application/json on every
 * write for the CSRF guard, which would corrupt a FormData body — the
 * browser must set the multipart boundary itself. So this sends the body
 * with NO explicit content type and lets fetch derive it from the FormData.
 */
export async function apiForm<T = unknown>(
  path: string,
  form: FormData,
  method = 'POST',
): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method,
    body: form,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(shownOrgId != null ? { [ORG_HEADER]: String(shownOrgId) } : {}),
      ...viewAsHeaders(),
    },
  })
  const body = await readBody(res)
  if (!res.ok) throw errorFrom(res.status, body)
  return unwrap(body) as T
}

export type Session = {
  user: {
    id: number
    email: string
    role: string
    is_admin: boolean
    is_platform_admin: boolean
    is_staff: boolean
    qa_enabled: boolean
  }
  org: { id: number; code: string; name: string } | null
  organizations: { id: number; code: string; name: string; logo_url: string | null }[]
  branding: { team_name: string | null; team_logo_url: string | null }
  is_professional: boolean
  team_id: number | null
  home_is_feed: boolean
  /** The Insights Workshop is in this person's rail: Priori staff
   *  (unless viewing as someone), else the club's workshop flag. */
  workshop?: boolean
  /** The UAT runner is in this person's club rail: a platform admin enabled
   *  them for UAT in this club (app/services/uat.py). */
  uat?: boolean
  show_tos_modal: boolean
  /** Who the app is acting AS, when that is not who is signed in. `user`
   *  above is already the target — the whole point is that every surface
   *  behaves as it does for them — so this is what the banner names. */
  impersonating: { id: number; email: string } | null
  /** Who is really signed in. Null unless impersonating. */
  actor: { id: number; email: string } | null
  may_impersonate: boolean
  /** Origin the media (video, parquet) is served from — presigned R2.
   *  The shell preconnects to it at boot so the TLS handshake overlaps
   *  the data fetches instead of following them. */
  media_origin?: string | null
}

export const getSession = () => api<Session>('/api/v1/session')
